import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import "./MonthPicker.css";

const MONTHS_BACK = 24;

const ITEM_HEIGHT = 41; // padding 20 + line-height ~21, must match .month-picker-option
const MENU_PADDING = 12; // 6px top + 6px bottom on .month-picker-menu

const MonthPicker = ({ dateRange, setDateRange }) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const months = useMemo(() => {
    const start = moment().startOf("month");
    const list = [];
    for (let i = 0; i <= MONTHS_BACK; i++) {
      const m = start.clone().subtract(i, "months");
      list.push({
        key: m.format("YYYY-MM"),
        label: m.format("MMMM YYYY"),
        start: m.clone().startOf("month").format("DD/MM/YYYY"),
        end: m.clone().endOf("month").format("DD/MM/YYYY"),
      });
    }
    return list;
  }, []);

  const selectedKey = useMemo(() => {
    if (!dateRange?.startDate) return moment().format("YYYY-MM");
    return moment(dateRange.startDate, "DD/MM/YYYY").format("YYYY-MM");
  }, [dateRange]);

  const selectedMonth = months.find((m) => m.key === selectedKey);
  const triggerLabel = selectedMonth ? selectedMonth.label : moment().format("MMMM YYYY");

  // Recompute menu position when opening + on resize while open. Close on scroll.
  useEffect(() => {
    if (!open) return;

    const updatePosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const margin = 12;
      const menuWidth = 240;
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      // Horizontal: prefer right-aligned with trigger; fall back to left-aligned
      // if that would push the menu past the viewport's left edge; finally clamp
      // to viewport bounds so it never sits off-screen.
      let left = rect.right - menuWidth;
      if (left < margin) left = rect.left;
      if (left + menuWidth > vw - margin) left = vw - menuWidth - margin;
      if (left < margin) left = margin;

      // Vertical: open below by default; if there isn't enough room below, open above.
      // Snap max-height to a whole number of items so the last visible row never
      // gets cut in half.
      const spaceBelow = vh - rect.bottom - margin;
      const spaceAbove = rect.top - margin;
      const desiredItems = 8;
      const fitItems = (avail) => {
        const items = Math.floor((avail - MENU_PADDING - 8) / ITEM_HEIGHT);
        const clamped = Math.max(3, Math.min(desiredItems, items));
        return clamped * ITEM_HEIGHT + MENU_PADDING;
      };

      let top;
      let maxHeight;
      if (spaceBelow >= 200 || spaceBelow >= spaceAbove) {
        maxHeight = fitItems(spaceBelow);
        top = rect.bottom + 8;
      } else {
        maxHeight = fitItems(spaceAbove);
        top = rect.top - maxHeight - 8;
      }

      setMenuPos({ top, left, maxHeight });
    };

    updatePosition();
    // Close on scroll, but ignore scroll events that originate inside the menu.
    const onScroll = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  const handleToggle = () => setOpen((o) => !o);

  const handleSelect = (m) => {
    setDateRange({ startDate: m.start, endDate: m.end });
    setOpen(false);
  };

  return (
    <div className="month-picker">
      <button
        ref={triggerRef}
        type="button"
        className="month-picker-trigger"
        onClick={handleToggle}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="17" rx="3" />
          <path d="M3 9H21" />
          <path d="M8 2V5" />
          <path d="M16 2V5" />
        </svg>
        <span>{triggerLabel}</span>
        <svg
          className={`month-picker-chevron ${open ? "open" : ""}`}
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && menuPos && (
        <>
          <div className="month-picker-backdrop" onClick={() => setOpen(false)} />
          <div
            ref={menuRef}
            className="month-picker-menu"
            style={{ top: menuPos.top, left: menuPos.left, maxHeight: menuPos.maxHeight }}
          >
            <div className="month-picker-list">
              {months.map((m) => {
                const isSelected = m.key === selectedKey;
                return (
                  <button
                    key={m.key}
                    type="button"
                    className={`month-picker-option ${isSelected ? "active" : ""}`}
                    onClick={() => handleSelect(m)}
                  >
                    <span>{m.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MonthPicker;
